const STORAGE_KEY = "incite:active-chat-thread:v1";
const ACTIVE_THREAD_TTL_MS = 6 * 60 * 60 * 1000;
const RECENT_TEMP_THREAD_RESTORE_WINDOW_MS = 10 * 60 * 1000;

const now = () => Date.now();

const storage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const ownerKeyForScope = (scope) => {
  if (!scope) return null;

  const userId = scope.user_id ?? scope.userId ?? null;
  if (userId) return `user:${userId}`;

  const sessionId =
    scope.uuid ??
    scope.preAccountUuid ??
    scope.current_session_id ??
    scope.sessionId ??
    null;
  if (sessionId) return `session:${sessionId}`;

  return null;
};

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const sessionOwnerKeysForScope = (scope) => {
  if (!scope) return [];

  return uniqueValues([
    scope.uuid,
    scope.preAccountUuid,
    scope.current_session_id,
    scope.sessionId,
  ]).map((sessionId) => `session:${sessionId}`);
};

const sessionFallbackOwnerKeysForScope = (scope) => {
  const ownerKey = ownerKeyForScope(scope);
  return sessionOwnerKeysForScope(scope).filter(
    (sessionOwnerKey) => sessionOwnerKey !== ownerKey,
  );
};

const storageKeyForScope = (scope) => {
  const ownerKey = ownerKeyForScope(scope);
  return ownerKey ? `${STORAGE_KEY}:${ownerKey}` : STORAGE_KEY;
};

const storageKeyForOwnerKey = (ownerKey) =>
  ownerKey ? `${STORAGE_KEY}:${ownerKey}` : STORAGE_KEY;

const normalizeThreadSnapshot = (thread, scope) => {
  const threadUuid = thread?.threadUuid || thread?.thread_uuid || null;
  if (!threadUuid) return null;

  const snapshot = {
    threadUuid,
    title: thread?.title || thread?.threadTitle || "Chat",
    savedAt: now(),
  };
  if (thread?.pendingRealThread) snapshot.pendingRealThread = true;

  const ownerKey = ownerKeyForScope(scope);
  if (ownerKey) snapshot.ownerKey = ownerKey;

  return snapshot;
};

export const isTempThreadUuid = (threadUuid) =>
  typeof threadUuid === "string" && threadUuid.startsWith("temp-");

const timestampForThread = (thread) => {
  const candidates = [
    thread?.lastActivityAt,
    thread?.last_activity_at,
    thread?.updatedAt,
    thread?.updated_at,
    thread?.createdAt,
    thread?.created_at,
  ];

  const timestamps = candidates
    .map((value) => {
      if (!value) return null;
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : null;
    })
    .filter((value) => value != null);

  return timestamps.length > 0 ? Math.max(...timestamps) : null;
};

const recentRealThreadForTempSnapshot = (rememberedThread, threads) => {
  if (!Array.isArray(threads) || threads.length === 0) return null;

  const savedAt = Number(rememberedThread?.savedAt);
  if (!Number.isFinite(savedAt)) return null;

  return (
    threads.find((thread) => {
      const threadUuid = thread?.threadUuid || thread?.thread_uuid || null;
      if (!threadUuid || isTempThreadUuid(threadUuid)) return false;

      const timestamp = timestampForThread(thread);
      if (!Number.isFinite(timestamp)) return false;

      return (
        timestamp >= savedAt - 60 * 1000 &&
        timestamp - savedAt <= RECENT_TEMP_THREAD_RESTORE_WINDOW_MS
      );
    }) || null
  );
};

export const resolveRememberedActiveChatThread = (
  rememberedThread,
  threads = [],
) => {
  const rememberedThreadUuid = rememberedThread?.threadUuid;
  if (!rememberedThreadUuid) {
    return null;
  }

  if (isTempThreadUuid(rememberedThreadUuid)) {
    if (rememberedThread?.pendingRealThread !== true) return null;
    return (
      recentRealThreadForTempSnapshot(rememberedThread, threads) || {
        threadUuid: rememberedThreadUuid,
        title: rememberedThread?.title || "New Thread",
        isAdminTargeted: false,
        pendingRealThread: true,
      }
    );
  }

  const existingThread = Array.isArray(threads)
    ? threads.find((thread) => thread?.threadUuid === rememberedThreadUuid)
    : null;
  if (existingThread) return existingThread;

  return {
    threadUuid: rememberedThreadUuid,
    title: rememberedThread?.title || "Chat",
    isAdminTargeted: false,
  };
};

export const rememberActiveChatThread = (thread, scope = null) => {
  const snapshot = normalizeThreadSnapshot(thread, scope);
  const store = storage();
  if (!snapshot || !store) return null;

  try {
    const key = storageKeyForScope(scope);
    store.setItem(key, JSON.stringify(snapshot));
    if (key !== STORAGE_KEY) store.removeItem(STORAGE_KEY);
    sessionFallbackOwnerKeysForScope(scope).forEach((sessionOwnerKey) => {
      store.removeItem(storageKeyForOwnerKey(sessionOwnerKey));
    });
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }

  return snapshot;
};

export const clearRememberedActiveChatThread = (scope = null) => {
  const store = storage();
  if (!store) return;

  try {
    const scopedKey = storageKeyForScope(scope);
    store.removeItem(scopedKey);
    store.removeItem(STORAGE_KEY);
    sessionFallbackOwnerKeysForScope(scope).forEach((sessionOwnerKey) => {
      store.removeItem(storageKeyForOwnerKey(sessionOwnerKey));
    });

    if (!ownerKeyForScope(scope)) {
      for (let i = store.length - 1; i >= 0; i -= 1) {
        const key = store.key(i);
        if (key?.startsWith(`${STORAGE_KEY}:`)) {
          store.removeItem(key);
        }
      }
    }
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
};

const readSnapshot = (store, key, expectedOwnerKey) => {
  try {
    const raw = store.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.threadUuid || !parsed?.savedAt) {
      store.removeItem(key);
      return null;
    }

    if (
      isTempThreadUuid(parsed.threadUuid) &&
      parsed.pendingRealThread !== true
    ) {
      store.removeItem(key);
      return null;
    }

    if (
      expectedOwnerKey &&
      parsed.ownerKey &&
      parsed.ownerKey !== expectedOwnerKey
    ) {
      store.removeItem(key);
      return null;
    }

    if (now() - Number(parsed.savedAt) > ACTIVE_THREAD_TTL_MS) {
      store.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    try {
      store.removeItem(key);
    } catch {
      // localStorage can be unavailable in private or restricted contexts.
    }
    return null;
  }
};

export const readRememberedActiveChatThread = (scope = null) => {
  const store = storage();
  if (!store) return null;

  const ownerKey = ownerKeyForScope(scope);
  const readTargets = [
    { key: storageKeyForScope(scope), ownerKey },
    ...sessionFallbackOwnerKeysForScope(scope).map((sessionOwnerKey) => ({
      key: storageKeyForOwnerKey(sessionOwnerKey),
      ownerKey: sessionOwnerKey,
    })),
  ];
  const seenKeys = new Set();

  for (const target of readTargets) {
    if (seenKeys.has(target.key)) continue;
    seenKeys.add(target.key);

    const snapshot = readSnapshot(store, target.key, target.ownerKey);
    if (snapshot) return snapshot;
  }

  return null;
};

export const readRememberedActiveChatThreadByUuid = (threadUuid) => {
  const store = storage();
  if (!store || !threadUuid) return null;

  try {
    for (let i = store.length - 1; i >= 0; i -= 1) {
      const key = store.key(i);
      if (key !== STORAGE_KEY && !key?.startsWith(`${STORAGE_KEY}:`)) {
        continue;
      }

      const snapshot = readSnapshot(store, key, null);
      if (snapshot?.threadUuid === threadUuid) return snapshot;
    }
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }

  return null;
};

export { ACTIVE_THREAD_TTL_MS, RECENT_TEMP_THREAD_RESTORE_WINDOW_MS };
